import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect } from "react";
import { BaseDialog } from "../../../components/Dialog/BaseDialog.tsx";
import { PimentoStar } from "../../../components/Icon/assets/PimentoStar.tsx";
import { BaseClickableIcon } from "../../../components/Icon/BaseClickableIcon.tsx";
import { useWorkspaceUuid } from "../../hooks/useWorkspaceUuid.ts";
import { SawtoothDown } from "./assets/SawtoothDown.tsx";
import { SawtoothUp } from "./assets/SawtoothUp.tsx";

export const PaymentDialog = ({
  isOpen,
  onOpenChange,
  topSectionContent,
  midSectionContent,
  bottomSectionContent,
  displayBottomSection,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  topSectionContent: ReactNode;
  midSectionContent: ReactNode;
  bottomSectionContent: ReactNode;
  displayBottomSection?: boolean;
}) => {
  const workspaceUuid = useWorkspaceUuid();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (isOpen) {
      // XXX: Invalidate the workspaces query when the dialog opens
      // This is to ensure that credits information displayed to users is always up to date
      void queryClient.invalidateQueries({
        queryKey: [`workspaces/${workspaceUuid}`],
      });
    }
  }, [isOpen, queryClient, workspaceUuid]);

  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      displayCloseButton={false}
      className=""
      content={
        <PaymentDialogContent
          onOpenChange={onOpenChange}
          topSectionContent={topSectionContent}
          midSectionContent={midSectionContent}
          bottomSectionContent={bottomSectionContent}
          displayBottomSection={displayBottomSection}
        />
      }
    />
  );
};

const PaymentDialogContent = ({
  onOpenChange,
  topSectionContent,
  midSectionContent,
  bottomSectionContent,
  displayBottomSection,
}: {
  onOpenChange: (open: boolean) => void;
  topSectionContent: ReactNode;
  midSectionContent: ReactNode;
  bottomSectionContent: ReactNode;
  displayBottomSection?: boolean;
}) => (
  <div className="relative w-[360px] flex-col gap-200">
    <div className="h-[176px] w-full flex-row justify-evenly pt-1000 px-1200 rounded-tl-300 rounded-tr-300 bg-brand-blue translate-y-400">
      <BaseClickableIcon
        name="X"
        iconClassName="stroke-button-primary-rest"
        className="absolute top-100 right-100"
        onClick={() => {
          onOpenChange(false);
        }}
      />
      {topSectionContent}
    </div>
    <div className="flex-col z-50">
      <SawtoothUp />
      <div className="flex-col justify-items-start px-600 py-800 gap-400 bg-surface-primary-rest">
        {midSectionContent}
      </div>
      <SawtoothDown />
    </div>
    {displayBottomSection && (
      <div className="relative flex-col justify-start gap-150 px-600 py-600 rounded-300 bg-surface-caution-primary-rest overflow-clip">
        {bottomSectionContent}
        <PimentoStar
          size={87}
          className="absolute bottom-[-60px] right-[-5px]"
        />
      </div>
    )}
  </div>
);
